import { useCallback } from "react";

import { BoxProps, Flex, FormLabel, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider } from "react-hook-form";
import { z } from "zod";

import { MaybePromise, Nullish } from "@/common";
import {
  EditablePopover,
  EditablePopoverProps,
  Form,
  FormFieldControl,
  InputFormField,
  useForm,
} from "@/components/form";

export interface EmailPopoverFieldProps
  extends Omit<BoxProps, "defaultValue" | "onChange"> {
  defaultValue?: Nullish<string>;
  hidePreviewLabel?: boolean;
  orientation?: EditablePopoverProps["orientation"];
  onChange: (value: Nullish<string>) => MaybePromise<void>;
}

export const EmailPopoverField = ({
  id,
  defaultValue = "",
  hidePreviewLabel = false,
  orientation = "vertical",
  onChange,
  ...props
}: EmailPopoverFieldProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const methods = useForm<EmailPopoverFieldValues>({
    defaultValues: { email: defaultValue ?? "" },
    resolver: zodResolver(schema),
  });

  const {
    formState: { isValid, isSubmitting },
  } = methods;

  const handleSubmit = useCallback(
    async (data: EmailPopoverFieldValues) => {
      await onChange?.(data.email);
      onClose();
    },
    [onChange, onClose]
  );

  return (
    <Flex
      maxW="full"
      direction={orientation === "vertical" ? "column" : "row"}
      alignItems={orientation === "vertical" ? "normal" : "center"}
      justifyContent={orientation === "vertical" ? "normal" : "space-between"}
      gap={1}
      {...props}
    >
      {!hidePreviewLabel && <FormLabel m={0}>Email address</FormLabel>}

      <EditablePopover
        isOpen={isOpen}
        defaultValue={defaultValue ?? ""}
        emptyLabel="Add an email address"
        orientation={orientation}
        isDisabled={!isValid}
        isLoading={isSubmitting}
        onOpen={onOpen}
        onClose={onClose}
        onSave={methods.handleSubmit(handleSubmit)}
      >
        <FormProvider {...methods}>
          <Form id={id} onSubmit={methods.handleSubmit(handleSubmit)}>
            <FormFieldControl name="email" mb={2}>
              <FormLabel>Email address</FormLabel>

              <InputFormField />
            </FormFieldControl>
          </Form>
        </FormProvider>
      </EditablePopover>
    </Flex>
  );
};

type EmailPopoverFieldValues = { email: string };

const schema = z.object({
  email: z
    .string()
    .email("The value must be a valid email address")
    .or(z.literal("")),
});
